<div class="org-layout-split" @fadeIn>
  <div class="members-page-left">

    <!-- Page header -->
    <div class="page-header">
      <div class="page-header-left">
        <h1 class="page-title">Team members</h1>
        <p class="page-subtitle">Manage your organization's members and roles</p>
      </div>
      <button class="btn-invite" (click)="showInviteForm = !showInviteForm" [class.active]="showInviteForm">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        Invite member
      </button>
    </div>

    <!-- License card -->
    <div class="license-card" *ngIf="license">
      <div class="license-card-row">
        <div class="license-stat">
          <span class="stat-label">Plan</span>
          <span class="stat-value">{{ license.license_type | titlecase }}</span>
        </div>
        <div class="license-stat">
          <span class="stat-label">Seats</span>
          <span class="stat-value" [class.seats-warning]="seatsFull">{{ seatsLabel }}</span>
        </div>
        <div class="license-stat">
          <span class="stat-label">Status</span>
          <span class="stat-badge" [class.badge-active]="license.status === 'active'" [class.badge-expired]="license.status !== 'active'">
            {{ license.status | titlecase }}
          </span>
        </div>
        <div class="license-stat" *ngIf="license.end_at">
          <span class="stat-label">Expires</span>
          <span class="stat-value">{{ formatDate(license.end_at) }}</span>
        </div>
      </div>
    </div>

    <!-- Invite form -->
    <div class="invite-form" *ngIf="showInviteForm" @fadeIn>
      <div class="invite-form-row">
        <input
          class="invite-input"
          type="email"
          placeholder="teammate@company.com"
          [(ngModel)]="inviteEmail"
          (keydown.enter)="sendInvite()"
          [disabled]="isSendingInvite"
        />
        <button
          class="btn-send-invite"
          (click)="sendInvite()"
          [disabled]="isSendingInvite || !inviteEmail.trim()"
        >
          <span *ngIf="!isSendingInvite">Send invite</span>
          <span *ngIf="isSendingInvite">Sending…</span>
        </button>
        <button class="btn-cancel-invite" (click)="showInviteForm = false; inviteEmail = ''; inviteError = ''">
          Cancel
        </button>
      </div>
      <p class="invite-error" *ngIf="inviteError">{{ inviteError }}</p>
      <p class="invite-hint" *ngIf="seatsFull"><i class="fa-solid fa-triangle-exclamation"></i> No seats available. Increase your seat limit to invite more members.</p>
    </div>

    <!-- Loading state -->
    <div class="loading-state" *ngIf="isLoading">
      <div class="spinner"></div>
      <span>Loading members…</span>
    </div>

    <!-- Members table -->
    <div class="table-wrapper" *ngIf="!isLoading">
      <table class="members-table">
        <thead>
          <tr>
            <th>Member</th>
            <th>Role</th>
            <th>Status</th>
            <th>Joined</th>
            <th class="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let member of members" @rowIn>
            <td>
              <div class="member-cell">
                <div class="member-avatar">{{ getMemberName(member).charAt(0).toUpperCase() }}</div>
                <div class="member-info">
                  <span class="member-name">{{ getMemberName(member) }}</span>
                  <span class="you-badge" *ngIf="isCurrentUser(member)">You</span>
                </div>
              </div>
            </td>
            <td>
              <span class="role-badge" [class.role-admin]="member.role === 'organizational_admin'">
                {{ getRoleLabel(member.role) }}
              </span>
            </td>
            <td>
              <span class="status-dot" [class.dot-active]="member.status === 'active'"></span>
              {{ member.status | titlecase }}
            </td>
            <td class="date-col">{{ formatDate(member.joined_at) }}</td>
            <td class="actions-col">
              <div class="action-buttons" *ngIf="!isCurrentUser(member)">
                <button
                  *ngIf="!member.isInvite"
                  class="action-btn"
                  (click)="toggleRole(member)"
                  [disabled]="isUpdatingRole === member.user_id"
                  [title]="member.role === 'organizational_admin' ? 'Demote to Member' : 'Promote to Admin'"
                >
                  {{ member.role === 'organizational_admin' ? 'Make Member' : 'Make Admin' }}
                </button>
                <button
                  class="action-btn action-btn--danger"
                  (click)="removeMember(member)"
                  [disabled]="isRemoving === member.user_id"
                >
                  {{ isRemoving === member.user_id ? 'Removing…' : (member.isInvite ? 'Revoke' : 'Remove') }}
                </button>
              </div>
              <span class="no-actions" *ngIf="isCurrentUser(member)">—</span>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="empty-state" *ngIf="members.length === 0">
        <p>No members yet. Invite your first teammate!</p>
      </div>
    </div>
  </div>

  <!-- Settings Pane on the Right -->
  <div class="settings-pane-right">
    <div class="settings-card">
      <div class="settings-header">
        <h2 class="settings-title">Meeting Summaries</h2>
        <p class="settings-subtitle">Automatically receive meeting summaries for members of your organization via email.</p>
      </div>

      <div class="form-group">
        <label class="form-label">Forward to (Email addresses)</label>
        <p class="field-hint">Comma-separated email addresses to receive BCC summaries.</p>
        <input 
          type="text" 
          class="settings-input" 
          placeholder="admin@company.com, leader@company.com" 
          [(ngModel)]="summaryEmailRecipients"
        />
      </div>

      <div class="form-group">
        <label class="form-label">Track meetings for</label>
        <div class="radio-group">
          <label class="radio-label" [class.active]="summaryEmailTargetType === 'all'">
            <input type="radio" name="targetType" value="all" [(ngModel)]="summaryEmailTargetType" />
            <span>All Members</span>
          </label>
          <label class="radio-label" [class.active]="summaryEmailTargetType === 'selected'">
            <input type="radio" name="targetType" value="selected" [(ngModel)]="summaryEmailTargetType" />
            <span>Specific Members</span>
          </label>
        </div>
      </div>

      <div class="member-checklist" *ngIf="summaryEmailTargetType === 'selected'">
        <label class="checklist-label">Select members to track:</label>
        <div class="checklist-items">
          <ng-container *ngFor="let m of members">
            <label class="check-item" *ngIf="!m.isInvite && !isCurrentUser(m)">
              <input 
                type="checkbox" 
                [checked]="summaryEmailTargetUsers.includes(m.user_id)"
                (change)="toggleTargetUser(m.user_id)"
              />
              <div class="custom-checkbox">
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
              <div class="check-item-avatar">{{ getMemberName(m).charAt(0).toUpperCase() }}</div>
              <div class="check-item-info">
                <span class="check-item-name">{{ getMemberName(m) }}</span>
                <span class="text-muted check-item-email">{{ m.email || 'No email' }}</span>
              </div>
            </label>
          </ng-container>
        </div>
      </div>

      <div class="settings-actions">
        <p class="error-msg" *ngIf="configError">{{ configError }}</p>
        <button class="btn-save" (click)="saveConfig()" [disabled]="isSavingConfig">
          {{ isSavingConfig ? 'Saving...' : 'Save Configuration' }}
        </button>
      </div>
    </div>
  </div>

</div>
